// Staff.tsx
import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from "react";
import {
  Table,
  ScrollArea,
  TextInput,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Modal,
  Pagination,
  Checkbox,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconUserPlus,
  IconEye,
  IconFile,
} from "@tabler/icons-react";
import classes from "./Staff.module.css";
import CreateStaff from "./CreateStaff";
import EditStaff from "./EditStaff";
import DeleteStaff from "./DeleteStaff";
import { RowDataStaff, AddStaffKPI } from "./types";
import { props } from "./props";
import { CustomTable } from "../Table/CustomTable";
import { staff } from "../types/RowDataTypes";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import AddKPI from "../StaffPage/AddKpi";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import { useNavigate } from "react-router-dom";
import Header from "../static/Header";
import GlobalContext from "../../api/GlobalProvider";
import getStaffTable from "../../DataDisplay/DataDisplayTable/StaffTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Name",
  EMAIL = "Email",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.NAME]: "fullname",
  [colFilter.EMAIL]: "email",
};

function Th({ children, sortable, reversed, sorted, onSort }: props) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.NAME.toLowerCase(),
    colFilter.EMAIL.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataStaff[] = [
  {
    staffId: "",
    name: "",
    phone_number: "",
    email: "",
    KPI: "",
    password: "",
    reenterPassword: "",
    action: "",
  },
];

export function Staff() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState<string>("");
  const [sortedData, setSortedData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataStaff | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [newStaff, setNewStaff] = useState<RowDataStaff>({
    staffId: "",
    name: "",
    phone_number: "",
    email: "",
    KPI: "",
    password: "",
    reenterPassword: "",
    action: "",
  });

  // Separate state for the modal related to adding staff
  const [isAddStaffFormVisible, setAddStaffFormVisible] = useState(false);

  useEffect(() => {
    // Reset newStaff when opening the "Add Staff" modal
    if (isAddStaffFormVisible) {
      setNewStaff({
        staffId: "",
        name: "",
        phone_number: "",
        email: "",
        KPI: "",
        password: "",
        reenterPassword: "",
        action: "",
      });
    }
  }, [isAddStaffFormVisible]);

  const [tableStateChanged, setTableStateChanged] = useState(0);
  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  //For Searching
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
    colFilters[colFilter.EMAIL],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    staff,
    getStaffTable().create()
  );

  const itemsOnPage = useRef(0);
  const totalRows = fetchedData.totalRows;
  const rowData: RowDataStaff[] | null = fetchedData.data as RowDataStaff[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataStaff, setRowDataStaff] = useState<RowDataStaff[] | null>(null);

  useEffect(() => {
    setRowDataStaff(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
  }, [rowData]);

  // Separate state for the modal related to viewing/editing staff
  const [selectedRow, setSelectedRow] = useState<RowDataStaff | null>(null);
  const [isEditMode, setEditMode] = useState(false);

  const [isDeleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [rowToDelete, setRowToDelete] = useState<RowDataStaff | null>(null);

  const [isViewMode, setViewMode] = useState(false);

  const nav = useNavigate();
  const { setStaffName } = useContext(GlobalContext);
  const handleView = (row: RowDataStaff) => {
    setStaffName(row.name);

    nav(`/staff/${row.staffId}/current`);
  };

  const handleEdit = (row: RowDataStaff) => {
    setSelectedRow(row);
    setEditMode(true);
    // Set the initial values for editing based on the selected row
    setNewStaff({
      staffId: "",
      name: row.name,
      phone_number: row.phone_number,
      email: row.email,
      KPI: row.KPI,
      password: row.password,
      reenterPassword: row.reenterPassword,
      action: row.action,
    });
  };

  const handleDelete = (row: RowDataStaff) => {
    setRowToDelete(row);
    setDeleteConfirmationVisible(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete) {
      axiosPrivate
        .post("/api/admin/staff/delete", {
          uid: rowToDelete!!.staffId,
        })
        .then((response) => {
          if (
            itemsOnPage.current == 1 &&
            currentPage !== 1 &&
            currentPage == Math.ceil(totalRows / itemsPerPage)
          )
            setCurrentPage((prev) => prev - 1);
          else changeTableState();
        })
        .catch((err) => {
          showErrorAlert("An error has occured");
        });
      setDeleteConfirmationVisible(false);
    }
  };

  const handleCancelDelete = () => {
    setRowToDelete(null);
    setDeleteConfirmationVisible(false);
  };

  const setSorting = (field: keyof RowDataStaff) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    // setSortedData(sortData(initialData, { sortBy: field, reversed, search }));
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const handleAddStaffs = () => {
    setAddStaffFormVisible(true);
  };

  const handleAddStaff = (newStaffData: RowDataStaff) => {
    // Update the state sortedData with the new staff data

    axiosPrivate
      .post("/api/admin/staff/create", {
        fullname: newStaffData.name,
        phone_number: newStaffData.phone_number,
        email: newStaffData.email,
        password: newStaffData.password,
      })
      .then((response) => {
        const data = response.data;
        const returnedData: RowDataStaff = {
          ...newStaffData,
          staffId: data.staffId,
        };
        if (itemsOnPage.current < itemsPerPage) {
          if (!reverseSortDirection)
            setRowDataStaff((prevData) => [returnedData, ...prevData!!]);
          else setRowDataStaff((prevData) => [...prevData!!, returnedData]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;
        showSuccessAlert("Staff Successfully Added");
      })
      .catch((error) => {
        let errMsg = error?.response?.data?.message
        showErrorAlert(errMsg ? errMsg : "Something Went Wrong");
      });

    // Close the modal
    setAddStaffFormVisible(false);
  };

  const handleSaveChanges = (
    editedData: RowDataStaff,
    originalData: RowDataStaff | null
  ) => {
    if (originalData) {
      const paramsToUpdate = {
        fullname: editedData.name,
        staffId: originalData.staffId,
        email: editedData.email,
        password: "",
      };

      if (
        editedData.password === editedData.reenterPassword &&
        editedData.password !== ""
      )
        paramsToUpdate["password"] = editedData.password;

      axiosPrivate
        .post("/api/admin/staff/update", paramsToUpdate)
        .then((response) => {
          const indexOfSelectedRow = rowDataStaff!!.findIndex(
            (row) => row === selectedRow
          );

          // Create a copy of the sortedData
          const updatedData = [...rowDataStaff!!];
          updatedData[indexOfSelectedRow] = {
            ...editedData,
            password: "",
            reenterPassword: "",
          };
        //   console.log(editedData);
          // // Set the updated data as the sortedData

          setRowDataStaff(updatedData);
          // Close the modal and exit edit mode
          setEditMode(false);
          showSuccessAlert("Staff Successfully Updated");
        })
        .catch((error) => {
          showErrorAlert("An error has occured");
        });
    }

    setEditMode(false);
  };

  const [isAddKPIFormVisible, setAddKPIFormVisible] = useState(false);

  const handleAddStaffKPI = (newStaffData: AddStaffKPI) => {
    axiosPrivate
      .post("/api/admin/kpi/create", {
        time_period_id: newStaffData.timePeriodId,
        duration: newStaffData.duration,
        kpi_objective_id: newStaffData.objectiveId,
        staff_id: JSON.stringify(newStaffData.staffId),
        target: newStaffData.targetPoints,
      })
      .then((response) => {
        showSuccessAlert(
          `Successfully added KPI for staff`
        );
      })
      .catch((error) => {
        // console.log("error " + error);
      });

    // Close the modal
    setAddKPIFormVisible(false);
  };

  const handleAddKPIs = () => {
    setAddKPIFormVisible(true);
  };

  const rowsToMap = rowDataStaff ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.name}</Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.email}</Table.Td>
      <Table.Td className={classes.actionButtons} style={{ width: "10%" }}>
        <button onClick={() => handleView(row)} className={classes.viewButton}>
          <IconEye /> View KPI
        </button>
        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>
        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.staffContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Staff" />
        <div className={classes.searchBarContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />

          <div className={classes.addButtonContainer}>
            <Button
              onClick={handleAddStaffs}
              className={classes.addStaffButton}
            >
              <IconUserPlus style={{ marginRight: "5px" }} />
              Create Staff
            </Button>
            <Button onClick={handleAddKPIs} className={classes.addStaffButton}>
              <IconFile style={{ marginRight: "5px" }} />
              Add KPI
            </Button>
          </div>
        </div>

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowData}
          rows={rows}
        />

        <div className={classes.footerContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>

        {/* Modal Form for Create, Edit, Delete */}
        <CreateStaff
          isVisible={isAddStaffFormVisible}
          onClose={() => setAddStaffFormVisible(false)}
          onAddStaff={(newStaffData) => handleAddStaff(newStaffData)}
        />

        {isViewMode && (
          <Modal
            opened={true}
            title="View Customer"
            onClose={() => setViewMode(false)}
            size="md"
            padding="lg"
          >
            <div className={classes.addCustomerForm}>
              {/* Display fields for viewing, but disable editing */}
              <TextInput label="Name" value={selectedRow?.name} />
              <TextInput label="Email" value={selectedRow?.email} />
              <TextInput label="KPI" value={selectedRow?.KPI} />
            </div>
          </Modal>
        )}

        {isEditMode && (
          <EditStaff
            isVisible={isEditMode}
            onClose={() => setEditMode(false)}
            onSaveChanges={(editedData) =>
              handleSaveChanges(editedData, selectedRow)
            }
            initialData={selectedRow as RowDataStaff}
          />
        )}
        {isDeleteConfirmationVisible && (
          <DeleteStaff
            isVisible={isDeleteConfirmationVisible}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        )}

        {/* Modal Form for Add KPI */}
        <AddKPI
          isVisible={isAddKPIFormVisible}
          onClose={() => setAddKPIFormVisible(false)}
          onAddStaffKpi={(newStaffData: AddStaffKPI) =>
            handleAddStaffKPI(newStaffData)
          }
        />
      </ScrollArea>
    </div>
  );
}

export default Staff;
